import { ArrowBack, Check, Close } from "@mui/icons-material";
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormControlLabel,
	Grid,
	IconButton,
	InputAdornment,
	LinearProgress,
	MenuItem,
	Switch,
	Typography,
} from "@mui/material";
import { addMonths, format, parseISO, startOfMonth } from "date-fns";
import { useCallback, useState } from "react";
import * as yup from "yup";

import { ToastContent } from "../../../../../shared/components";
import { IVFormErros, useVForm, VForm, VTextField } from "../../../../../shared/forms";
import { VMaskTextField } from "../../../../../shared/forms/VMaskTextField";
import { useToast } from "../../../../../shared/hooks";
import { IInstallmentProposal, ProposalService } from "../../../../../shared/services/api/proposal/ProposalService";
import { globalRemoveMask } from "../../../../../shared/utils";
import { formatDateForInput } from "../../../../../shared/utils/format-date";
import { returnMessageError } from "../../../../../shared/utils/returnMessageError";
import { ModalFinancialPreview } from "./ModalFinancialPreview";

interface ModalFinancialProps {
	isOpenModal: boolean;
	isClose: () => void;
	isSuperAdmin: boolean;
	isUpdate: () => void;
	rows: any;
}

interface IFormFinancial {
	amount: string;
	dueDate: string;
	obs: string;
	status?: string;
	paymentMethod?: string;
	number?: string;
	type?: string;
	typeAmount: string;
	proposalId?: string;
	clientId?: string;
	addBonus?: boolean; // ✅ aqui
}

export const ModalFinancialLaunch = ({ isOpenModal, isClose, isSuperAdmin, isUpdate, rows }: ModalFinancialProps) => {
	const { formRef } = useVForm();
	const toastHandler = useToast();
	const [isLoading, setIsLoading] = useState(false);
	const [rowsInstallment, setRowsInstallment] = useState<IInstallmentProposal[]>([]);
	const [isFinancialModalValidityDateEnabled, setIsFinancialModalValidityDateEnabled] = useState(true);
	const [isRepeatEnabled, setIsRepeatEnabled] = useState(false);
	const [previewOpen, setPreviewOpen] = useState(false);
	const [previewItems, setPreviewItems] = useState<any[]>([]);
	const [finalPayloads, setFinalPayloads] = useState<any[]>([]);
	const [addBonus, setAddBonus] = useState(false);

	const firstDayOfMonth = format(startOfMonth(new Date()), "yyyy-MM-dd");

	const formValidationSchema: yup.SchemaOf<IFormFinancial> = yup.object().shape({
		amount: yup.string().required("Campo Obrigatório"),
		dueDate: yup.string().required("Campo Obrigatório"),
		obs: yup.string().required("Campo Obrigatório"),
		typeAmount: yup.string().required("Campo Obrigatório"),
		status: yup.string().notRequired(),
		paymentMethod: yup.string().notRequired(),
		type: yup.string().notRequired(),
		number: yup.string().notRequired(),
		proposalId: yup.string().notRequired(),
		clientId: yup.string().notRequired(),
		addBonus: yup.boolean().notRequired(), // ✅ aqui

	});

	const canBillInTheSelectedMonth = useCallback(
		(month: number) => {
			const installmentInMonth = rowsInstallment.find((installment) => {
				const installmentMonth = parseInt(installment.dueDate.split("-")[1]);
				return installmentMonth === month;
			});
			return !installmentInMonth || installmentInMonth.status === "canceled";
		},
		[rowsInstallment]
	);

	const handleValidityDateChange = (date: string) => {
		if (!date) return;
		const [year, month] = date.split("-").map(Number);
		const validityDate = new Date(year, month - 1, 1);

		if (!canBillInTheSelectedMonth(validityDate.getMonth() + 1)) {
			const nextMonthDate = addMonths(validityDate, 1);
			formRef.current?.setFieldValue("dueDate", formatDateForInput(nextMonthDate));
			toastHandler.present({
				durationMs: 5000,
				type: "warning",
				position: "top-right",
				messageOrContent: <ToastContent title="Aviso" content="Já existe uma fatura no mês selecionado. O registro será lançado para o próximo mês." />,
			});
		}
	};

	const handleTypeAmountChange = (typeAmount: string) => {
		const isEnabled = typeAmount !== "add";
		setIsFinancialModalValidityDateEnabled(isEnabled);

		if (!isEnabled) {
			const now = new Date();
			const canBill = canBillInTheSelectedMonth(now.getMonth() + 1);
			const targetDate = canBill ? now : addMonths(now, 1);
			formRef.current?.setFieldValue("dueDate", formatDateForInput(targetDate));

			if (!canBill) {
				toastHandler.present({
					durationMs: 5000,
					type: "warning",
					position: "top-right",
					messageOrContent: (
						<ToastContent title="Aviso" content="Já existe uma fatura no mês corrente. O registro será lançado para o mês seguinte." />
					),
				});
			}
		}
	};

	const handleSave = async (data: any) => {
		try {
			const repeat = isRepeatEnabled ? Number(data.repeat || 1) : 1;
			const repeatMode = isRepeatEnabled ? data.repeatMode || "monthly" : "monthly";
			const { repeat: _repeat, repeatMode: _repeatMode, ...cleanedData } = data;

			await formValidationSchema.validate(cleanedData, { abortEarly: false });

			//const baseDate = parseISO(cleanedData.dueDate);
			const baseDate = parseISO(cleanedData.dueDate.substring(0, 10));
			const payloads = Array.from({ length: repeat }).map((_, i) => {
				const dueDate = repeatMode === "monthly" ? addMonths(baseDate, i) : baseDate;

				return {
					...cleanedData,
					amount: globalRemoveMask(cleanedData.amount),
					paymentMethod: "billet",
					status: "pending",
					type: "monthly_payment",
					number: (rowsInstallment.length + i + 1).toString(),
					proposalId: rows.id,
					clientId: rows.clientId,
					dueDate: formatDateForInput(dueDate) + " 03:00:00",
					addBonus,
				};
			});

			const preview = payloads.map((item) => ({
				dueDate: item.dueDate.split(" ")[0],
				amount: item.amount,
				typeAmount: item.typeAmount,
				obs: item.obs,
				addBonus: item.addBonus,
			}));
			console.log("Payloads to be sent:", payloads);
			setFinalPayloads(payloads);
			setPreviewItems(preview);
			setPreviewOpen(true);
		} catch (error) {
			setIsLoading(false);

			const validationErrors: IVFormErros = {};
			if (error instanceof yup.ValidationError) {
				error.inner.forEach((err) => {
					if (err.path) validationErrors[err.path] = err.message;
				});
				formRef.current?.setErrors(validationErrors);
			}

			const { titleMessage, contentMessage } = returnMessageError(error);
			toastHandler.present({
				type: "error",
				position: "top-right",
				messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
			});
		}
	};

	const handleAddBonusChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const checked = event.target.checked;
		setAddBonus(checked);
		formRef.current?.setFieldValue("addBonus", checked);
	}

	const handleConfirmFromPreview = async () => {
		try {
			setIsLoading(true);
			for (const payload of finalPayloads) {
				const result = await ProposalService.createInstallment(payload);
				if (result instanceof Error) throw result;
			}

			setIsLoading(false);
			toastHandler.present({
				type: "success",
				messageOrContent: "Lançamento(s) efetuado(s) com sucesso!",
				position: "top-center",
			});

			setPreviewOpen(false);
			isClose();
			isUpdate();
		} catch (error) {
			setIsLoading(false);
			const { titleMessage, contentMessage } = returnMessageError(error);
			toastHandler.present({
				type: "error",
				position: "top-right",
				messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
			});
		}
	};

	return (
		<>
			<Dialog open={isOpenModal} onClose={isClose} maxWidth="sm" fullWidth>
				<DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
					<Typography variant="h6" fontWeight={400} color="primary.main">
						Lançamento Financeiro
					</Typography>
					<IconButton onClick={isClose}>
						<Close />
					</IconButton>
				</DialogTitle>

				<Divider />

				<DialogContent sx={{ px: 4, pt: 3 }}>
					<VForm ref={formRef} onSubmit={handleSave} onPointerEnterCapture={() => {}} onPointerLeaveCapture={() => {}} placeholder="">
						<Grid container spacing={2}>
							{isLoading && (
								<Grid item xs={12}>
									<LinearProgress />
								</Grid>
							)}

							<Grid item xs={12}>
								<VTextField fullWidth label="Descrição" name="obs" />
							</Grid>

							<Grid item xs={4}>
								<VTextField
									fullWidth
									label="Tipo de lançamento"
									name="typeAmount"
									select
									onChange={(e) => handleTypeAmountChange(e.target.value)}
								>
									<MenuItem value="discount">Desconto</MenuItem>
									<MenuItem value="add">Acréscimo</MenuItem>
									<MenuItem value="parcel">Parcela</MenuItem>
								</VTextField>
							</Grid>

							<Grid item xs={4}>
								<VMaskTextField
									fullWidth
									label="Valor"
									name="amount"
									mask="real"
									InputProps={{
										startAdornment: <InputAdornment position="start">R$</InputAdornment>,
									}}
								/>
							</Grid>

							<Grid item xs={4}>
								<VTextField
									fullWidth
									label="Vencimento"
									name="dueDate"
									type="date"
									inputProps={{ min: firstDayOfMonth }}
									onChange={(e) => handleValidityDateChange(e.target.value)}
									disabled={!isFinancialModalValidityDateEnabled}
								/>
							</Grid>

							<Grid item xs={12}>
								<FormControlLabel
									control={
										<Switch
											checked={isRepeatEnabled}
											onChange={(e) => {
												const checked = e.target.checked;
												setIsRepeatEnabled(checked);
												if (checked) {
													formRef.current?.setFieldValue("repeat", 1);
													formRef.current?.setFieldValue("repeatMode", "monthly");
												} else {
													formRef.current?.setFieldValue("repeat", "");
													formRef.current?.setFieldValue("repeatMode", "");
												}
											}}
											color="primary"
										/>
									}
									label="Repetir lançamento"
								/>
							</Grid>

							<Grid item xs={12}>
								<FormControlLabel
									control={
										<Switch
											checked={addBonus}
											onChange={handleAddBonusChange}
											color="primary"
										/>
									}
									label="Lançar Bonificação da parcela"
								/>
							</Grid>

							{isRepeatEnabled && (
								<>
									<Divider sx={{ width: "100%", mb: 2 }} />
									<Grid item xs={4}>
										<VTextField fullWidth type="number" name="repeat" label="Total de parcelas" inputProps={{ min: 1 }} defaultValue={1} />
									</Grid>

									<Grid item xs={8}>
										<VTextField fullWidth select name="repeatMode" label="Distribuição dos lançamentos" defaultValue="monthly">
											<MenuItem value="same">Mesmo mês</MenuItem>
											<MenuItem value="monthly">Mensalmente</MenuItem>
										</VTextField>
									</Grid>
								</>
							)}
						</Grid>
					</VForm>
				</DialogContent>

				<DialogActions sx={{ px: 4, pb: 3 }}>
					<Button variant="outlined" startIcon={<ArrowBack />} onClick={isClose}>
						Voltar
					</Button>
					<Button variant="contained" color="primary" startIcon={<Check />} onClick={() => formRef.current?.submitForm()}>
						Confirmar
					</Button>
				</DialogActions>
			</Dialog>

			<ModalFinancialPreview open={previewOpen} onClose={() => setPreviewOpen(false)} onConfirm={handleConfirmFromPreview} items={previewItems} />
		</>
	);
};
